import { ContentCard } from '@braze/react-native-sdk';
import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import {
  BrazeCardType,
  BrazeContentCard,
  BrazeSdkCard,
  BrazeSuccessProductOfferCard,
} from 'services/engagement/braze/types';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';

export const BrazeExtraKeys = {
  SUCCESS_SCREEN_ID: 'success_screen_id',
  PRIMARY_BUTTON_TEXT: 'primary_button_text',
  BACKGROUND_COLOR: 'background_color',
  ILLUSTRATION_KEY: 'illustration_key',
  ILLUSTRATION_BACKGROUND_COLOR: 'illustration_background_color',
  DISCLOSURE_TEXT: 'disclosure_text',
  DISCLOSURE_TAGLINE: 'disclosure_tagline',
  DISCLOSURE_LINK_TEXT: 'disclosure_link_text',
  TYPE: 'type',
  BOTTOM_SHEET_CARD: 'bottom_sheet_card',
} as const;

function transformCardData(card: ContentCard): BrazeContentCard | null {
  if (card.type === 'Classic') {
    if (
      (card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] !== undefined &&
        card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] !== null) ||
      card.extras[BrazeExtraKeys.BOTTOM_SHEET_CARD] === 'true'
    ) {
      return null;
    }
    return {
      id: card.id,
      type: BrazeCardType.CLASSIC,
      title: card.title,
      description: card.cardDescription,
      isDismissible: card.dismissible,
      url: card.url,
      imageUrl: card.image,
      expiresAt: convertTimestampToDate(card.expiresAt),
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: null,
    };
  } else if (card.type === 'Captioned') {
    return {
      id: card.id,
      type: BrazeCardType.CAPTIONED_IMAGE,
      title: card.title,
      description: card.cardDescription,
      isDismissible: card.dismissible,
      url: card.url,
      imageUrl: card.image,
      expiresAt: convertTimestampToDate(card.expiresAt),
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: null,
    };
  } else if (card.type === 'ImageOnly') {
    return {
      id: card.id,
      type: BrazeCardType.IMAGE_ONLY,
      title: null,
      description: null,
      isDismissible: card.dismissible,
      url: card.url,
      imageUrl: card.image,
      expiresAt: convertTimestampToDate(card.expiresAt),
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: null,
    };
  }
  return null;
}

export function transformBrazeContentCard(
  sdkCard: BrazeSdkCard,
): BrazeContentCard | null {
  const card = sdkCard as ContentCard;
  if (card.extras[BrazeExtraKeys.TYPE] === 'carousel_marketing_cards') {
    return null;
  }
  return transformCardData(card);
}

export function transformMarketingCarouselCards(
  sdkCard: BrazeSdkCard,
): BrazeContentCard | null {
  const card = sdkCard as ContentCard;
  if (card.extras[BrazeExtraKeys.TYPE] === 'carousel_marketing_cards') {
    return transformCardData(card);
  }
  return null;
}

export function transformBrazeBottomSheetCard(
  sdkCard: BrazeSdkCard,
): BrazeBottomSheetProps | null {
  const card = sdkCard as ContentCard;

  if (card.type !== 'Classic') {
    return null;
  }
  if (card.extras.bottom_sheet_card !== 'true') {
    return null;
  }
  return {
    id: card.id,
    disclosureLinkText: card.extras[
      BrazeExtraKeys.DISCLOSURE_LINK_TEXT
    ] as string,
    disclosureTagline: card.extras[BrazeExtraKeys.DISCLOSURE_TAGLINE] as string,
    illustrationKey: card.extras[
      BrazeExtraKeys.ILLUSTRATION_KEY
    ] as IllustrationKeyType,
    title: card.title,
    subtext: card.cardDescription,
    disclosureText: card.extras[BrazeExtraKeys.DISCLOSURE_TEXT] as string,
    primaryButtonLabel: card.extras[
      BrazeExtraKeys.PRIMARY_BUTTON_TEXT
    ] as string,
    url: card.url,
    sdkCardWeb: null,
  };
}

export const transformBrazeSuccessProductOfferCard = (
  sdkCard: BrazeSdkCard,
): { key: string; card: BrazeSuccessProductOfferCard } | null => {
  const card = sdkCard as ContentCard;
  if (
    card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] === null ||
    card.type !== 'Classic'
  ) {
    return null;
  }
  return {
    key: card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] as string,
    card: {
      id: card.id,
      titleText: card.title,
      bodyText: card.cardDescription ?? undefined,
      buttonText: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      url: card.url,
      illustrationKey: card.extras[
        BrazeExtraKeys.ILLUSTRATION_KEY
      ] as IllustrationKeyType,
      illustrationBackgroundColor: card.extras[
        BrazeExtraKeys.ILLUSTRATION_BACKGROUND_COLOR
      ] as string,
      sdkCardWeb: null,
    },
  };
};

/**
 * Converts a UNIX timestamp to a Date object.
 */
function convertTimestampToDate(timestamp: number): Date {
  // Convert the UNIX timestamp to milliseconds
  const milliseconds = timestamp * 1000;
  return new Date(milliseconds);
}
